import { useState } from "react";
import { useAccountant } from "@/contexts/AccountantContext";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { BottomButtonContainer, useKipaToast } from "@/components/kipa";
import CurrencySelect from "@/components/CurrencySelect";
import { clearPendingOnboarding } from "@/lib/onboardingFlags";
import { detectDefaultCountryCode, COUNTRY_CODES, formatPhoneWithCode } from "@/lib/phoneCountry";
import { isLogoTooLarge, logoTooLargeMessage } from "@/lib/logoUpload";
import KipaIcon from "@/components/kipa/KipaIcon";
import {
  Building2,
  Briefcase,
  Footprints,
  GraduationCap,
  HeartPulse,
  Scissors,
  Shirt,
  ShoppingBag,
  Smartphone,
  Truck,
  UtensilsCrossed,
  Wheat,
  Wrench,
} from "lucide-react";

const PRESET_NAMES = [
  { name: "Kola", desc: "Friendly and reliable" },
  { name: "Ada", desc: "Sharp and precise" },
  { name: "Amina", desc: "Calm and thorough" },
];

const BUSINESS_TYPES = [
  { id: "fashion", label: "Fashion", icon: Shirt },
  { id: "food-catering", label: "Food & Catering", icon: UtensilsCrossed },
  { id: "hair-beauty", label: "Hair & Beauty", icon: Scissors },
  { id: "retail", label: "Retail", icon: ShoppingBag },
  { id: "shoes", label: "Shoes", icon: Footprints },
  { id: "electronics", label: "Electronics", icon: Smartphone },
  { id: "agriculture", label: "Agriculture", icon: Wheat },
  { id: "healthcare", label: "Healthcare", icon: HeartPulse },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "logistics", label: "Logistics", icon: Truck },
  { id: "consulting", label: "Consulting", icon: Briefcase },
  { id: "services", label: "Services", icon: Wrench },
];

function capitalize(s) {
  const t = s.trim();
  if (!t) return "";
  return t[0].toUpperCase() + t.slice(1).toLowerCase();
}

/** After sign-up: business setup → accountant naming (value slides run before auth). */
export default function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(0); // 0=biz, 1=naming
  const { updateSettings } = useAccountant();
  const [bizName, setBizName] = useState("");
  const [bizType, setBizType] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [businessLogo, setBusinessLogo] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState(() => detectDefaultCountryCode());

  const finishOnboarding = async (accountantName) => {
    const name = capitalize(accountantName);
    await updateSettings({
      business_name: bizName.trim() || "My Business",
      business_type: bizType || null,
      currency,
      accountant_name: name,
      ...(businessLogo ? { business_logo: businessLogo } : {}),
      ...(ownerPhone.trim() ? { owner_phone: formatPhoneWithCode(phoneCode, ownerPhone) } : {}),
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos",
      onboarding_complete: true,
    });
    localStorage.setItem("bizpa_onboarded", "1");
    clearPendingOnboarding();
    onComplete();
  };

  if (step === 0) {
    return (
      <BusinessSetup
        bizName={bizName}
        setBizName={setBizName}
        bizType={bizType}
        setBizType={setBizType}
        currency={currency}
        setCurrency={setCurrency}
        businessLogo={businessLogo}
        setBusinessLogo={setBusinessLogo}
        ownerPhone={ownerPhone}
        setOwnerPhone={setOwnerPhone}
        phoneCode={phoneCode}
        setPhoneCode={setPhoneCode}
        onContinue={() => setStep(1)}
      />
    );
  }
  return <AccountantNaming onFinish={finishOnboarding} />;
}

// ───────── Business setup ─────────
function BusinessSetup({
  bizName,
  setBizName,
  bizType,
  setBizType,
  currency,
  setCurrency,
  businessLogo,
  setBusinessLogo,
  ownerPhone,
  setOwnerPhone,
  phoneCode,
  setPhoneCode,
  onContinue,
}) {
  const { showToast } = useKipaToast();
  const [customType, setCustomType] = useState("");
  const effectiveType = customType.trim() || bizType;
  const canContinue = bizName.trim().length >= 2 && effectiveType.length >= 2 && currency;

  const pickPreset = (t) => {
    setBizType(t);
    setCustomType("");
  };

  const onCustomTypeChange = (value) => {
    setCustomType(value);
    if (value.trim()) setBizType("");
  };

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Please choose an image file.", "warning");
      return;
    }
    if (isLogoTooLarge(file)) {
      showToast(logoTooLargeMessage(), "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBusinessLogo(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const handleContinue = () => {
    setBizType(effectiveType);
    onContinue();
  };

  return (
    <div className="flex-1 flex flex-col" style={{ background: "var(--bg)", minHeight: "100vh" }} data-testid="onb-business">
      <div className="flex-1 overflow-y-auto px-6 py-10">
        <div className="text-[var(--text-2xl)] font-black text-[var(--color-text-primary)] mb-2">Tell us about your business</div>
        <div className="text-[var(--text-sm)] text-[var(--color-text-secondary)] mb-6">Takes less than a minute.</div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">Business Name</div>
          <input
            className="w-full bg-white border-[1.5px] border-[var(--br)] rounded-2xl px-4 py-3.5 text-[15px] outline-none focus:border-[var(--ac)] font-sans"
            value={bizName}
            onChange={(e) => setBizName(e.target.value)}
            placeholder="e.g. Amaka's Fashion House"
            data-testid="onb-biz-name"
          />
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-2">What do you sell?</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {BUSINESS_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => pickPreset(t.label)}
                data-testid={`onb-biz-type-${t.id}`}
                className={`px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all inline-flex items-center gap-1.5 ${
                  bizType === t.label && !customType.trim()
                    ? "bg-[var(--ac)] text-white border-[var(--ac)]"
                    : "bg-white text-[var(--tx2)] border-[var(--br)]"
                }`}
                style={{ border: bizType === t.label && !customType.trim() ? "1.5px solid var(--ac)" : "1.5px solid var(--br)" }}
              >
                <KipaIcon
                  icon={t.icon}
                  size="xs"
                  color={bizType === t.label && !customType.trim() ? "#fff" : "var(--color-primary)"}
                />
                {t.label}
              </button>
            ))}
          </div>
          <div className="text-[var(--text-xs)] font-bold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">
            Or describe your business
          </div>
          <input
            className="s-inp"
            value={customType}
            onChange={(e) => onCustomTypeChange(e.target.value)}
            placeholder="e.g. Pharmacy, School, Law firm, Photography…"
            data-testid="onb-biz-type-custom"
          />
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">Currency</div>
          <div className="text-[11px] text-[var(--tx3)] mb-2">Type to search — e.g. Naira, USD, ₦</div>
          <CurrencySelect
            value={currency}
            onChange={setCurrency}
            testId="onb-currency"
          />
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-2">
            Business logo / profile picture
          </div>
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl border-[1.5px] border-[var(--br)] bg-white flex items-center justify-center overflow-hidden flex-shrink-0"
              data-testid="onb-logo-preview"
            >
              {businessLogo ? (
                <img src={businessLogo} alt="Business logo" className="w-full h-full object-cover" />
              ) : (
                <KipaIcon icon={Building2} size="lg" color="var(--color-primary)" />
              )}
            </div>
            <div className="flex-1">
              <label className="inline-block bg-white border-[1.5px] border-[var(--br)] rounded-xl px-4 py-2.5 text-[13px] font-semibold text-[var(--ac)] cursor-pointer">
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoPick}
                  data-testid="onb-logo-input"
                />
              </label>
              {businessLogo && (
                <button
                  type="button"
                  onClick={() => setBusinessLogo("")}
                  className="block mt-2 text-[12px] text-[var(--tx3)] font-semibold"
                  data-testid="onb-logo-remove"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">Phone number (optional)</div>
          <div className="flex gap-2">
            <select
              className="bg-white border-[1.5px] border-[var(--br)] rounded-2xl px-2 py-3 text-[14px] outline-none"
              value={phoneCode}
              onChange={(e) => setPhoneCode(e.target.value)}
              data-testid="onb-phone-code"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <input
              className="flex-1 bg-white border-[1.5px] border-[var(--br)] rounded-2xl px-4 py-3 text-[15px] outline-none focus:border-[var(--ac)] font-sans"
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="801 234 5678"
              data-testid="onb-phone"
            />
          </div>
        </div>
      </div>
      <BottomButtonContainer>
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="onb-biz-continue"
          className="kipa-btn kipa-btn-primary kipa-btn-lg kipa-btn-full kipa-btn-press disabled:opacity-45"
        >
          Choose Your Accountant →
        </button>
      </BottomButtonContainer>
    </div>
  );
}

// ───────── Accountant naming ─────────
function AccountantNaming({ onFinish }) {
  const { showToast } = useKipaToast();
  const [selectedCard, setSelectedCard] = useState(null);
  const [customName, setCustomName] = useState("");
  const [pulse, setPulse] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const effectiveName = customName.trim() || selectedCard || "";
  const isValid = effectiveName.length >= 2 && effectiveName.length <= 20;
  const showLengthError = customName.length === 1;

  const pickCard = (name) => {
    setSelectedCard(name);
    setCustomName("");
  };

  const onCustomChange = (v) => {
    // Reject numbers + special chars
    if (/[^a-zA-Z\s'-]/.test(v)) return;
    if (v.length > 20) return;
    setCustomName(v);
    if (v) setSelectedCard(null);
  };

  const submit = async () => {
    if (!isValid) {
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
      return;
    }
    setSubmitting(true);
    try {
      await onFinish(effectiveName);
    } catch (e) {
      showToast("Failed to save: " + e.message, "error");
      setSubmitting(false);
    }
  };

  const buttonLabel = !effectiveName
    ? "Choose a name first"
    : isValid ? `Meet ${capitalize(effectiveName)} →` : "Choose a name first";

  return (
    <div className="flex-1 flex flex-col" style={{ background: "var(--bg)", minHeight: "100vh" }} data-testid="onb-naming">
      <div className="flex-1 overflow-y-auto px-6 py-10">
        <div className="text-[26px] font-black text-[var(--tx)] mb-2 leading-tight">
          What would you like to call your accountant?
        </div>
        <div className="text-[14px] text-[var(--tx2)] mb-6 leading-relaxed">
          This is the AI that will manage your business numbers every day. Give them a name that feels right to you.
        </div>

        <div className={`grid grid-cols-3 gap-2 mb-6 ${pulse ? "onb-pulse" : ""}`}>
          {PRESET_NAMES.map((p) => {
            const active = selectedCard === p.name;
            return (
              <button
                key={p.name}
                onClick={() => pickCard(p.name)}
                data-testid={`onb-name-${p.name.toLowerCase()}`}
                className="rounded-2xl p-4 text-center transition-all"
                style={{
                  background: active ? "var(--ac)" : "white",
                  border: active ? "1.5px solid var(--ac)" : "1.5px solid var(--br)",
                  boxShadow: active ? "0 4px 14px rgba(13,110,110,0.22)" : "0 2px 8px rgba(28,25,23,0.04)",
                }}
              >
                <div className="text-[16px] font-extrabold mb-1" style={{ color: active ? "#fff" : "var(--tx)" }}>
                  {p.name}
                </div>
                <div className="text-[10px] leading-tight" style={{ color: active ? "rgba(255,255,255,0.85)" : "var(--tx3)" }}>
                  {p.desc}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-[var(--br)]" />
          <div className="text-[11px] text-[var(--tx3)] font-semibold uppercase tracking-wider">
            — or choose your own name —
          </div>
          <div className="flex-1 h-px bg-[var(--br)]" />
        </div>

        <div className="mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">Your own choice</div>
          <input
            className="w-full bg-white border-[1.5px] border-[var(--br)] rounded-2xl px-4 py-3.5 text-[15px] outline-none focus:border-[var(--ac)] font-sans"
            value={customName}
            onChange={(e) => onCustomChange(e.target.value)}
            placeholder="Type any name you like..."
            maxLength={20}
            data-testid="onb-custom-name"
          />
          {showLengthError && (
            <div className="text-[12px] text-[var(--rd)] mt-1.5" data-testid="onb-length-err">
              Name must be at least 2 characters
            </div>
          )}
        </div>
      </div>

      <BottomButtonContainer>
        <button
          onClick={submit}
          disabled={submitting || !isValid}
          data-testid="onb-finish"
          className="kipa-btn kipa-btn-primary kipa-btn-lg kipa-btn-full kipa-btn-press disabled:opacity-45"
        >
          {submitting ? "Saving…" : buttonLabel}
        </button>
      </BottomButtonContainer>

      <style>{`
        .onb-pulse { animation: onbPulse 0.6s ease-out; }
        @keyframes onbPulse {
          0%, 100% { transform: scale(1); }
          40% { transform: scale(1.02); }
          70% { transform: scale(0.99); }
        }
      `}</style>
    </div>
  );
}
